<template>
  <div class="managed_instruction_assignments_table">
    <div class="d-flex justify-end ma-2 ml-0">
      <v-btn
        v-if="isMultiUser"
        color="primary"
        class="mr-2"
        @click="openMultiUserDialog"
      >
        <v-icon left>mdi-swap-horizontal</v-icon>
        {{ $t("multiUser.button") }}
      </v-btn>
      <v-btn
        v-if="canCreateHere"
        color="secondary"
        outlined
        @click.stop="onAddInstruction"
      >
        {{
          $t("managedInstructions.createInstructionDialog.createInstruction")
        }}
        <v-icon right>add</v-icon>
      </v-btn>
    </div>

    <template v-if="mineInstructions.length">
      <div class="mi-section-title">{{ $t("managedInstructions.mineTitle") }}</div>
      <v-data-table
        class="mb-9"
        :headers="headers"
        :items="mineInstructions"
        :loading="allManagedInstructionsPending"
        :footer-props="{
          itemsPerPageOptions: tableDataGetter.pagination.rowsPerPageItems,
          itemsPerPageText: $t('general.rowsPerPage'),
        }"
        :items-per-page="-1"
        @click:row="goToInstruction($event._id, allManagedInstructions)"
      >
        <template v-slot:item.previewImage="{ item }">
          <v-img :src="getPreviewSrc(item)" width="100" height="100" class="my-2 rounded elevation" />
        </template>
        <template v-slot:item.name="{ item }">
          {{ getFieldByLocale(item.name) }}
        </template>
        <template v-slot:item.description="{ item }">
          {{ getFieldByLocale(item.description) }}
        </template>
        <template v-slot:item.assigned="{ item }">
          <v-chip color="primary" outlined class="px-4" ripple>{{ item.assignedCount }}</v-chip>
        </template>
        <template v-slot:item.finished="{ item }">
          <v-chip color="primary" outlined class="px-4" ripple>{{ item.assignmentDoneCount }}</v-chip>
        </template>
        <template v-slot:item.open="{ item }">
          <v-chip color="red" outlined class="px-4" ripple>{{ item.openAssignmentCount }}</v-chip>
        </template>
      </v-data-table>
    </template>

    <template v-if="globalInstructions.length">
      <div class="mi-section-title">{{ $t("managedInstructions.globalTitle") }}</div>
      <v-data-table
        class="mb-9"
        :headers="globalHeaders"
        :items="globalInstructions"
        :loading="allManagedInstructionsPending"
        :footer-props="{
          itemsPerPageOptions: tableDataGetter.pagination.rowsPerPageItems,
          itemsPerPageText: $t('general.rowsPerPage'),
        }"
        :items-per-page="-1"
        @click:row="goToInstruction($event._id, allManagedInstructions)"
      >
        <template v-slot:item.previewImage="{ item }">
          <v-img :src="getPreviewSrc(item)" width="100" height="100" class="my-2 rounded elevation" />
        </template>
        <template v-slot:item.name="{ item }">
          {{ getFieldByLocale(item.name) }}
        </template>
        <template v-slot:item.description="{ item }">
          {{ getFieldByLocale(item.description) }}
        </template>
      </v-data-table>
    </template>

    <managed-instruction-create-dialog ref="createDialog" @saved="initPage()" />
    <multiUserDialog
      v-model="multiUserOpen"
      entityType="managedInstruction"
      :availableItems="transferItems"
      itemText="_displayName"
      itemValue="_id"
      :clients="transferClients"
      :currentClientId="currentClientId"
      @confirm="executeTransfer"
      entityLabel="multiUser.dialog.managedInstructions"
    />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import ManagedInstructionCreateDialog from "@/views/managedInstruction/managedInstructionCreateDialog.vue";
import multiUserDialog from "../../components/_utils/dataTableHelpers/actions/multiUserDialog.vue";

const PUBLIC_PREFIX = "https://s3.cloud2.c-dev.io/sp-templating";

export default {
  components: {
    ManagedInstructionCreateDialog,
    multiUserDialog,
  },
  computed: {
    ...mapGetters([
      "allManagedInstructions",
      "allManagedInstructionsPending",
      "tableDataGetter",
    ]),
    headers() {
      return [
        { text: "", value: "previewImage", align: "left" },
        {
          text: this.$t("managedInstructions.tableHeader.instruction"),
          value: "name",
        },
        {
          text: this.$t("managedInstructions.tableHeader.description"),
          value: "description",
        },
        {
          text: this.$t("managedInstructions.tableHeader.assigned"),
          value: "assigned",
          align: "center",
        },
        {
          text: this.$t("managedInstructions.tableHeader.finished"),
          value: "finished",
          align: "center",
        },
        {
          text: this.$t("managedInstructions.tableHeader.open"),
          value: "open",
          align: "center",
        },
      ];
    },
    globalHeaders() {
      return [
        { text: "", value: "previewImage", align: "left", sortable: false },
        {
          text: this.$t("managedInstructions.tableHeader.instruction"),
          value: "name",
        },
        {
          text: this.$t("managedInstructions.tableHeader.description"),
          value: "description",
        },
      ];
    },
    mineInstructions() {
      return (this.allManagedInstructions || []).filter((item) => item.isMine);
    },
    globalInstructions() {
      return (this.allManagedInstructions || []).filter((item) => !item.isMine);
    },
    transferItems() {
      const backendLang = this.localeMultiUserMap[this.$i18n.locale] || "GER";

      return (this.allManagedInstructions || [])
        .filter((item) => item.type === "local")
        .map((item) => {
          const names = Array.isArray(item.name) ? item.name : [];

          let nameEntry =
            names.find((n) => n.language === backendLang) ||
            names.find((n) => n.language === "GER") ||
            names[0];

          let displayName = nameEntry?.name;
          if (!displayName && typeof item.name === "string") {
            displayName = item.name;
          }

          return {
            ...item,
            _displayName: displayName || "—",
          };
        });
    },
  },
  async created() {
    this.initPage();

    const user = await this.$store.dispatch("getUserInfo");
    this.currentClientId = user?.client?._id || "";
    this.isMultiUser = user?.role === "MultiUser";
  },
  data: () => ({
    selectedInstructionId: null,
    localeMap: {
      de: "GER",
      en: "EN",
    },
    localeMultiUserMap: {
      de: "GER",
      en: "ENG",
    },
    previewUrlById: Object.create(null),
    previewLoadingById: Object.create(null),
    currentClientId: "",
    isMultiUser: false,
    multiUserOpen: false,
    transferClients: [],
  }),
  watch: {
    allManagedInstructions: {
      immediate: true,
      handler(items) {
        this.hydratePreviewImages(items || []);
      },
    },
  },
  methods: {
    initPage() {
      this.$store.dispatch("getAllManagedInstructions");
    },
    isPublicPreview(url) {
      return typeof url === "string" && url.includes(PUBLIC_PREFIX);
    },
    getPreviewSrc(item) {
      const url = item?.previewImage;
      if (!url) return "";
      if (this.isPublicPreview(url)) return url;
      return this.previewUrlById[item._id] || "";
    },

    async hydratePreviewImages(items) {
      const tasks = [];

      for (const item of items) {
        const id = item?._id;
        const url = item?.previewImage;
        const urlId = item?.previewImageId;

        if (!id || !url) continue;
        if (this.isPublicPreview(url)) continue;
        if (this.previewUrlById[id]) continue;
        if (this.previewLoadingById[id]) continue;

        this.$set(this.previewLoadingById, id, true);

        tasks.push(
          this.$store
            .dispatch("getPresignedPublicUrl", { path: url, id: urlId })
            .then((presignedUrl) => {
              if (presignedUrl)
                this.$set(this.previewUrlById, id, presignedUrl);
            })
            .catch(() => {})
            .finally(() => {
              this.$set(this.previewLoadingById, id, false);
            }),
        );
      }

      if (tasks.length) await Promise.all(tasks);
    },
    getFieldByLocale(fieldArray) {
      if (typeof fieldArray === "object") {
        const backendLang = this.localeMap[this.$i18n.locale];
        let field = fieldArray.find((item) => item?.language === backendLang);
        if (!field) {
          field = fieldArray.find((item) => item?.language === "ENG");
        }
        return field ? field.name || field.description : "";
      } else {
        return fieldArray;
      }
    },
    onAddInstruction() {
      this.$refs.createDialog.openDialog();
    },
    openTagAssignmentDialog(instructionId) {
      this.selectedInstructionId = instructionId;
      this.$nextTick(() => this.$refs.tagAssignmentDialog.openDialog());
    },
    goToInstruction(instructionId, items) {
      const currentInstruction = items.find(
        (item) => item._id === instructionId,
      );

      let name = "";
      if (typeof currentInstruction?.name === "string") {
        name = currentInstruction.name;
      } else if (Array.isArray(currentInstruction?.name)) {
        const list = currentInstruction.name;
        name = (list.find((n) => n.language === "GER") || list[0])?.name || "";
      }
      this.$router.push({
        name: "managed-instruction-details",
        params: {
          instructionId: instructionId,
          instructionName: name,
        },
      });
    },

    async openMultiUserDialog() {
      try {
        const res = await this.$store.dispatch("getTransferClients");
        this.transferClients = res.data || [];
      } catch (e) {
        return;
      }
      this.multiUserOpen = true;
    },

    async executeTransfer(payload) {
      try {
        const result = await this.$store.dispatch("transferEntities", payload);

        const parts = [];
        if (result.totalSuccess > 0) {
          parts.push(
            this.$t("multiUser.result.success", {
              number: result.totalSuccess,
            }),
          );
        }
        if (result.totalConflict > 0) {
          parts.push(
            this.$t("multiUser.result.error", {
              number: result.totalConflict,
            }),
          );
        }
        if (result.totalError > 0) {
          parts.push(
            this.$t("multiUser.result.error", { number: result.totalError }),
          );
        }

        this.$store.commit("CREATE_SNACKBAR", parts.join(" · "));

        if (payload.action === "move" && result.totalSuccess > 0) {
          this.initPage();
        }
      } catch (e) {
        console.error("Transfer failed:", e);
      }
    },
  },
};
</script>

<style scoped>
.mi-section-title {
  font-size: 1.15rem;
  font-weight: 500;
  margin: 12px 0 8px;
}
</style>

<style>
div.managed_instruction_assignments_table th[role="columnheader"].sortable {
  white-space: nowrap;
}

div.managed_instruction_assignments_table tr {
  cursor: pointer;
}
</style>
